import './anchors.style.scss';

interface ArticleAnchor {
    id: string;
    title: string;
    level: number;
}

export class ArticleAnchorsComponent extends HTMLElement {
    private articleContainer: HTMLElement = document.querySelector('.content-section')!;
    private headingElements: HTMLElement[] = [];
    private headings: ArticleAnchor[] = [];
    private anchorLinks: HTMLAnchorElement[] = [];
    private dropdown: HTMLSelectElement | null = null;
    private linksContainer: HTMLDivElement | null = null;
    private intersectionObserver: IntersectionObserver | null = null;

    constructor() {
        super();
        this.className = 'tbw-article-anchors';
    }

    connectedCallback() {
        this.initializeAnchors();
        this.setupPremiumContentListener();
    }

    private initializeAnchors() {
        this.headings = this.scanHeadings(this.articleContainer);
        this.bindIdsToHeadings();
        this.createMobileDropdown();
        this.createDesktopAnchors();
        this.setupActiveTracking();
    }

    private setupPremiumContentListener() {
        document.addEventListener("premiumContentLoaded", () => {
            const premiumContainer = document.querySelector('tbw-premium-content');
            if (!premiumContainer) {
                console.warn('Premium content container not found');
                return;
            }

            const premiumHeadings = this.scanHeadings(premiumContainer, this.headings.length);
            if (premiumHeadings.length === 0) {
                // console.log('No premium headings found');
                return;
            }

            this.appendAnchors(premiumHeadings);
        });
    }

    private scanHeadings(container: Element, startIndex: number = 0): ArticleAnchor[] {
        const headingElements = Array.from(container.querySelectorAll('h1, h2, h3')) as HTMLElement[];

        return headingElements.map((element, index) => {
            const heading: ArticleAnchor = {
                id: `article-anchor-${startIndex + index}`,
                title: element.innerHTML.replace(/<[^>]*>?/g, ''),
                level: this.getHeadingLevel(element)
            };

            // Bind ID immediately
            element.setAttribute('id', heading.id);

            // Track if this is initial scan
            if (startIndex === 0) {
                this.headingElements.push(element);
            }

            return heading;
        });
    }

    private getHeadingLevel(element: HTMLElement): number {
        const tagName = element.tagName.toLowerCase();
        return tagName === 'h1' ? 1 : tagName === 'h2' ? 2 : 3;
    }

    private bindIdsToHeadings() {
        this.headings.forEach((heading, index) => {
            this.headingElements[index]?.setAttribute('id', heading.id);
        });
    }

    private appendAnchors(newHeadings: ArticleAnchor[]) {
        // Get the heading elements for observer
        const newElements = newHeadings.map(h => document.getElementById(h.id)!).filter(Boolean);

        // Update internal state
        this.headings.push(...newHeadings);
        this.headingElements.push(...newElements);

        // Append to UI
        newHeadings.forEach(heading => {
            this.addDropdownOption(heading);
            this.addDesktopAnchor(heading);
        });

        // Add to observer
        newElements.forEach(element => this.intersectionObserver?.observe(element));
    }

    private createMobileDropdown() {
        if (this.headings.length === 0) return;

        const container = document.createElement('div');
        container.className = 'tbw-anchors-dropdown-container';

        this.dropdown = document.createElement('select');
        this.dropdown.className = 'tbw-anchors-dropdown';
        this.dropdown.id = 'tbw-anchors-dropdown-container';

        // Default option
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = 'Select a section...';
        this.dropdown.appendChild(defaultOption);

        // Add all headings
        this.headings.forEach(heading => this.addDropdownOption(heading));

        // Handle navigation
        this.dropdown.addEventListener('change', (e) => this.handleDropdownChange(e));

        container.appendChild(this.dropdown);
        this.appendChild(container);
    }

    private addDropdownOption(heading: ArticleAnchor) {
        if (!this.dropdown) return;

        const option = document.createElement('option');
        option.value = heading.id;
        option.textContent = heading.level === 2 ? `  ${heading.title}` : heading.title;
        option.dataset.targetId = heading.id;
        this.dropdown.appendChild(option);
    }

    private handleDropdownChange(e: Event) {
        const target = e.target as HTMLSelectElement;
        if (target.value) {
            const element = document.getElementById(target.value);
            if (element) {
                element.scrollIntoView({ behavior: 'instant', block: 'start' });
                window.location.hash = target.value;
            }
        }
    }

    private createDesktopAnchors() {
        if (this.headings.length === 0) return;

        this.linksContainer = document.createElement('div');
        this.linksContainer.className = 'tbw-anchors-links-container';

        this.headings.forEach(heading => this.addDesktopAnchor(heading));

        this.appendChild(this.linksContainer);
    }

    private addDesktopAnchor(heading: ArticleAnchor) {
        if (!this.linksContainer) return;

        const anchor = document.createElement('a');
        anchor.href = `#${heading.id}`;
        anchor.innerHTML = heading.title;
        anchor.classList.add(`tbw-article-anchor-${heading.level}`);
        anchor.dataset.targetId = heading.id;

        anchor.addEventListener('click', (e) => {
            this.handleAnchorClick(e, heading.id)
            window.tbwServices.analyticsService.track("article_anchor_click")
        });

        this.anchorLinks.push(anchor);
        this.linksContainer.appendChild(anchor);
    }

    private handleAnchorClick(e: Event, headingId: string) {
        e.preventDefault();
        const targetElement = document.getElementById(headingId);
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'instant', block: 'start' });
            window.history.pushState(null, '', `#${headingId}`);
        }
    }

    private setupActiveTracking() {
        const updateActive = (id: string) => {
            // Update desktop links
            this.anchorLinks.forEach(anchor => {
                anchor.classList.toggle('is-active', anchor.dataset.targetId === id);
            });

            // Update dropdown
            if (this.dropdown) {
                this.dropdown.value = id;
            }
        };

        const updateFromHash = () => {
            const hash = window.location.hash.substring(1);
            if (hash) updateActive(hash);
        };

        // Initial state
        updateFromHash();

        // Listen for hash changes
        window.addEventListener('hashchange', updateFromHash);

        // Track scroll position
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        updateActive(entry.target.id);
                    }
                });
            },
            {
                threshold: 0,
                rootMargin: '0% 0px -80% 0px'
            }
        );

        this.headingElements.forEach(heading => this.intersectionObserver!.observe(heading));
    }
}
